import { Modal, ActionButton, DataTable, Pill } from 'components/commons';

import locale from 'localization';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './books-of-accounts-modal.module.scss';
import { useApi, useFilter } from 'hooks';
import { getTransactionCustomersByBatchId } from 'apis/transaction.api';
import { booksOfAccountsMembersModalFilterState } from './books-of-accounts-members-modal-filter-state';
import { formatCurrency } from 'utils';
import { transactionCustomerColumns } from './books-of-accounts-members-modal-columns';
import { prettifyProductType } from 'utils/pretty.utils';
import ProductType from 'enums/product-type';

const BooksOfAccountsMembersModal = ({ batchId, accountId, close, active }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  const { modifyFilters, requestState } = useFilter(booksOfAccountsMembersModalFilterState());

  const {
    request: getTransactionCustomerRequest,
    loading: fetchingTransactionCustomer,
    result: getTransactionCustomerResult = { transactionCustomers: [], count: 0 }
  } = useApi({
    api: getTransactionCustomersByBatchId,
    pageError: true,
    params: {
      batchId,
      accountId
    }
  });

  const preparedTransactionCustomersData = useMemo(() => {
    const { transactionCustomers } = getTransactionCustomerResult;
    if (transactionCustomers.length > 0) {
      const preparedData = transactionCustomers.map((c) => {
        const map = new Map();
        map.set('customerAccountNumber', c?.customerAccountNumber || '--');
        map.set('firstName', c?.firstName || '--');
        map.set('lastName', c?.lastName || '--');
        map.set('productType', c?.productType || '--');
        map.set('amount', formatCurrency(c?.amount || 0));

        map.set(
          'productType',
          <div>
            <Pill
              grass={c.productType === ProductType.Savings}
              cheddar={c.productType === ProductType.FixedDeposit}
              sky={c.productType === ProductType.SharedCapital}
            >
              {prettifyProductType(c.productType)}
            </Pill>
          </div>
        );

        return map;
      });
      return preparedData;
    }
    return [];
  }, [getTransactionCustomerResult]);

  const fetchCustomerClaims = useCallback(
    (rs) => {
      getTransactionCustomerRequest();
    },
    [getTransactionCustomerRequest]
  );

  useEffect(() => {
    if (active && !isLoaded) {
      fetchCustomerClaims(requestState);
      setIsLoaded(true);
    }
  }, [active, fetchCustomerClaims, isLoaded, requestState]);

  useEffect(() => {
    if (close && !active) {
      setIsLoaded(false);
    }
  }, [active, close, fetchCustomerClaims]);

  const onChangePageCb = useCallback(
    (page) => {
      const { requestState } = modifyFilters({ page });
      fetchCustomerClaims(requestState);
    },
    [fetchCustomerClaims, modifyFilters]
  );

  const onChangePageSizeCb = useCallback(
    (perPage) => {
      const { requestState } = modifyFilters({ perPage, page: 1 });
      fetchCustomerClaims(requestState);
    },
    [fetchCustomerClaims, modifyFilters]
  );

  return (
    <Modal active={active} title={`Members`} close={close} className={styles.membersModal}>
      <div className={styles.content}>
        <DataTable
          loading={fetchingTransactionCustomer}
          columns={transactionCustomerColumns}
          data={preparedTransactionCustomersData}
          dataCount={getTransactionCustomerResult.count}
          onChangePage={onChangePageCb}
          onChangePageSize={onChangePageSizeCb}
          isPaginationHidden={true}
        />
      </div>
      <ActionButton
        right
        items={[
          {
            text: locale.cancel,
            onClick: close
          }
        ]}
      />
    </Modal>
  );
};

export default BooksOfAccountsMembersModal;
